/**
 * How the import screen reads a CSV's header row for the mapping step.
 *
 * The backend parses the same file again (functions/python/app/application/
 * service/attendee_import.py), and the two must agree on every rule here, or
 * the mapping offers columns the import then cannot find:
 *
 * - UTF-8 only, with or without a byte-order mark. Anything else is refused
 *   with the same advice the backend gives.
 * - The separator is ";" when the header line has more semicolons than
 *   commas, otherwise ",". German Excel writes ";" for both of its CSV exports.
 * - Header names are trimmed.
 */

export const UNDECODABLE_CSV =
  'The file is not UTF-8 text. In Excel, save it as "CSV UTF-8" and upload it again.';

/** storage.rules refuses an import upload above this many bytes. */
export const MAX_IMPORT_BYTES = 10 * 1024 * 1024;

export const TOO_LARGE_CSV =
  "The file is larger than 10 MB. Split it into smaller files and import each one.";

export type ImportCsvDelimiter = "," | ";";

export function importCsvDelimiter(headerLine: string): ImportCsvDelimiter {
  const count = (separator: string) => headerLine.split(separator).length - 1;
  return count(";") > count(",") ? ";" : ",";
}

/** Decode as UTF-8, dropping a BOM, or return null when the bytes are not UTF-8. */
export function decodeImportCsv(bytes: ArrayBuffer): string | null {
  try {
    // TextDecoder drops a leading BOM by default, as Python's utf-8-sig does.
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return null;
  }
}

/** The file's bytes. FileReader rather than Blob.arrayBuffer, which jsdom lacks. */
export function readFileBytes(file: Blob): Promise<ArrayBuffer> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () =>
      reject(reader.error ?? new Error("File read failed"));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * A header name reduced for matching against a column's label and aliases:
 * accents, case, spaces and punctuation removed, so "E-Mail", "e mail" and
 * "Email" all compare equal.
 */
export function squashHeader(text: string): string {
  return text
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

export function parseImportHeaders(text: string): string[] {
  const firstLine = text.replace(/^\uFEFF/, "").split(/\r?\n/)[0] ?? "";
  const delimiter = importCsvDelimiter(firstLine);
  return firstLine
    .split(delimiter)
    .map((header) => header.trim().replace(/^"|"$/g, "").trim())
    .filter(Boolean);
}

/** One data row, numbered the way a spreadsheet numbers it: the header is 1. */
export interface ImportCsvRow {
  rowNumber: number;
  /** Cell text by trimmed header name. A short row reads its missing cells as "". */
  values: Record<string, string>;
  /** True when the row has non-empty cells past the last header. */
  overflow: boolean;
}

export type ParsedImportCsv =
  | { ok: true; headers: string[]; rows: ImportCsvRow[] }
  | { ok: false; problem: string };

/**
 * The whole file as rows, for an import that runs in the browser.
 *
 * The attendee import only needs the header row here, because its backend
 * parses the file again with Python's `csv` module. The agenda import has no
 * backend parse, so this reads every record, with the rules that module
 * applies under `strict=True`, so both imports treat one file the same way:
 *
 * - The separator and the byte-order mark are handled as above.
 * - A field opening with `"` is quoted: it may hold the separator and line
 *   breaks, and `""` inside it is one quote. Anything but a separator or a
 *   line break after its closing quote is refused, as is a file that ends
 *   inside one.
 * - A quote inside an unquoted field is kept as a character.
 * - Rows whose cells are all blank are skipped, like the `;;;` rows a
 *   spreadsheet writes below its data, and still counted, so row numbers
 *   stay the spreadsheet's.
 * - Two headers with the same trimmed name are refused.
 */
export function parseImportCsv(text: string): ParsedImportCsv {
  const source = text.replace(/^\uFEFF/, "");
  const firstLine = source.split(/\r\n|\n|\r/, 1)[0] ?? "";
  const delimiter = importCsvDelimiter(firstLine);
  const records = readCsvRecords(source, delimiter);
  if (!Array.isArray(records)) return { ok: false, problem: records.problem };
  const [headerRecord, ...dataRecords] = records;
  if (!headerRecord || headerRecord.every((cell) => !cell.trim()))
    return { ok: false, problem: "The file has no header row." };
  const headers = headerRecord.map((header) => header.trim());
  const named = headers.filter(Boolean);
  if (new Set(named).size !== named.length)
    return {
      ok: false,
      problem: "Two columns in the header row have the same name.",
    };

  const rows: ImportCsvRow[] = [];
  dataRecords.forEach((record, index) => {
    if (record.every((cell) => !cell.trim())) return;
    const values: Record<string, string> = {};
    headers.forEach((header, column) => {
      if (header) values[header] = (record[column] ?? "").trim();
    });
    rows.push({
      rowNumber: index + 2,
      values,
      overflow: record.slice(headers.length).some((cell) => cell.trim()),
    });
  });
  return { ok: true, headers: named, rows };
}

/**
 * Split CSV text into records of raw cells. An empty line is a record with one
 * empty cell, so it keeps its place in the numbering.
 */
function readCsvRecords(
  text: string,
  delimiter: ImportCsvDelimiter,
): string[][] | { problem: string } {
  const records: string[][] = [];
  let record: string[] = [];
  let field = "";
  let at = 0;
  // The record count so far, for the line an error names.
  const line = () => records.length + 1;

  const endField = () => {
    record.push(field);
    field = "";
  };
  const endRecord = () => {
    endField();
    records.push(record);
    record = [];
  };

  while (at < text.length) {
    if (field === "" && text[at] === '"') {
      // A quoted field: read to its closing quote.
      at++;
      for (;;) {
        if (at >= text.length)
          return {
            problem: `Row ${line()} opens a quote that never closes. Check that the file is saved as CSV.`,
          };
        if (text[at] === '"') {
          if (text[at + 1] === '"') {
            field += '"';
            at += 2;
            continue;
          }
          at++;
          break;
        }
        field += text[at];
        at++;
      }
      const next = text[at];
      if (
        next !== undefined &&
        next !== delimiter &&
        next !== "\n" &&
        next !== "\r"
      )
        return {
          problem: `Row ${line()} has text after a closing quote. Check that the file is saved as CSV.`,
        };
      // End of file right after the quote: close the record here, because the
      // check below the loop cannot tell a quoted "" from no field at all.
      if (next === undefined) {
        endRecord();
        return records;
      }
      continue;
    }
    const char = text[at];
    if (char === delimiter) {
      endField();
      at++;
    } else if (char === "\r" || char === "\n") {
      endRecord();
      at += char === "\r" && text[at + 1] === "\n" ? 2 : 1;
    } else {
      field += char;
      at++;
    }
  }
  if (field !== "" || record.length > 0) endRecord();
  return records;
}
